export function missingWidget(widgetName) {
  $('body').addClass(`${widgetName}-no-data`);
}

// ****************************************************************************************************************************************************
// Utility function to determine whether a given URL belongs to the current domain (local) or is external

// Usage ==> isLocalUrl(slide.URL);
export function isLocalUrl(urlToCheck) {
  return urlToCheck?.includes(SITEBASEURL) ?? false;
}

// ****************************************************************************************************************************************************
// Utility function to detect window size against a defined breakpoint size

// Usage ==> validateWindowSize(800);

export function validateWindowSize(breakpointValue) {
  const breakpoint = globalThis.matchMedia(`(min-width: ${breakpointValue}px)`);
  return breakpoint.matches;
}

// ****************************************************************************************************************************************************
// Utility function to add attributes to links based on whether they are local, external, or empty

function getLinkType(url) {
  if (!url) return 'empty';
  if (isLocalUrl(url)) return 'local';
  return 'external';
}

function getAttributesString(urlToCheck, placeholder) {
  const type = getLinkType(urlToCheck);

  switch (type) {
    case 'empty':
      return placeholder
        ? `aria-label="${placeholder}" aria-disabled="true"`
        : `aria-disabled="true"`;

    case 'local':
      return placeholder ? `aria-label="${placeholder}"` : '';

    case 'external': {
      const label = placeholder ? `aria-label="${placeholder} (opens in a new tab)"` : '';
      return [`target="_blank"`, `rel="noopener noreferrer"`, label].filter(Boolean).join(' ');
    }
  }
}

function setAttributesDOM($el, urlToCheck, placeholder) {
  const type = getLinkType(urlToCheck);

  switch (type) {
    case 'empty':
      $el.removeAttr('href').addClass('void-link');
      $el.attr('aria-disabled', 'true');
      if (placeholder) $el.attr('aria-label', placeholder);
      break;

    case 'local':
      if (placeholder) $el.attr('aria-label', placeholder);
      break;

    case 'external':
      $el.attr({ target: '_blank', rel: 'noopener noreferrer' });
      if (placeholder) $el.attr('aria-label', `${placeholder} (opens in a new tab)`);
      break;
  }
}

export function updateLinksAttributes(urlToCheck, placeholder, $el = null) {
  if ($el) {
    setAttributesDOM($el, urlToCheck, placeholder);
    return;
  }

  return getAttributesString(urlToCheck, placeholder);
}

// ****************************************************************************************************************************************************
// Utility function to update card link behavior based on widget data (replaces modifyLinkBehavior + validateLinks)

export function updateCardLinkBehavior(widgetData, $cards, isMobileBreakpoint = false) {
  $cards.each(function (itemIndex) {
    const $card = $(this);
    const $buttons = $card.find('.buttonwrap');
    const cardIsLink = $card.is('a');

    // Extract URL from widget data (PurchaseURL takes precedence over URL)
    const itemData = widgetData?.items?.[itemIndex] || {};
    const urlToCheck = itemData.PurchaseURL || itemData.URL || '';

    // Update link attributes based on URL type
    updateLinksAttributes(urlToCheck, '', $card);

    // Handle mobile-specific behavior
    if (isMobileBreakpoint && cardIsLink && $buttons.length) {
      $buttons.remove();
    }
  });
}

// ****************************************************************************************************************************************************
// Makes any non-button element keyboard and screen-reader accessible as a button

// Usage ==> makeAccessibleButton($('.my-toggle'), 'Open menu', () => { ... });

export function makeAccessibleButton($el, label, onActivate) {
  if (!$el.length) return;

  if (!$el.is('button')) {
    $el.attr({
      tabindex: 0,
      role: 'button',
      'aria-label': label,
    });
  }

  $el.off('keydown.accessibleBtn').on('keydown.accessibleBtn', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      $(this).trigger('click');
    }
  });

  if (onActivate) {
    $el.off('click.accessibleBtn').on('click.accessibleBtn', onActivate);
  }
}

// ****************************************************************************************************************************************************
// Pairs with makeAccessibleButton to handle the close side of a toggle pair: rebinds the keyboard listener with .off() first
// to prevent accumulation across menu opens, and exposes a `focusReturn.close` event the caller can trigger once the menu
// has actually closed to return focus to the originating toggle.

// Usage ==> closeAccessibility($closeToggle, $openToggle);
//           makeAccessibleButton($closeToggle, 'Close menu', () => {
//             requestAnimationFrame(() => {
//               if (menuIsClosed) $closeToggle.trigger('focusReturn.close');
//             });
//           });

export function closeAccessibility($closeToggle, $openToggle) {
  $closeToggle.off('keydown.accessibleClose').on('keydown.accessibleClose', function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      $(this).trigger('click');
    }
  });

  $closeToggle.off('focusReturn.close').on('focusReturn.close', () => {
    $openToggle.get(0)?.focus();
  });
}

// ****************************************************************************************************************************************************
// Sets a CSS custom property (--theme-header-height) that tracks the header's rendered height via ResizeObserver
//
// - Desktop (>= header-breakpoint, i.e. >= 1150px): only the generalmessage + top-links
//   push the slider down so the logo + navbar can sit on top of it.
// - Mobile (< header-breakpoint): the slider needs the full header height.
//   Breakpoint value mirrors $header-breakpoint in _variables.scss ('xl' = 1150px).

const HEADER_BREAKPOINT_PX = 1150;

export function observeHeaderHeight() {
  if (!('ResizeObserver' in globalThis)) return { destroy() {} };

  const mq = globalThis.matchMedia(`(min-width: ${HEADER_BREAKPOINT_PX}px)`);
  let currentRo = null;

  function getTargets() {
    const selectors = mq.matches ? ['.generalmessage', '.header .top-links'] : ['.header'];

    return selectors.map((s) => document.querySelector(s)).filter(Boolean);
  }

  function setup() {
    if (currentRo) currentRo.disconnect();

    const targets = getTargets();
    if (!targets.length) return;

    let lastHeight = null;
    function apply() {
      const h = targets.reduce((sum, el) => sum + Math.round(el.getBoundingClientRect().height), 0);
      if (h !== lastHeight) {
        lastHeight = h;
        document.documentElement.style.setProperty('--theme-header-height', `${h}px`);
      }
    }

    currentRo = new ResizeObserver(apply);
    targets.forEach((el) => currentRo.observe(el));
    apply();
  }

  setup();
  mq.addEventListener('change', setup);

  return {
    destroy: () => {
      currentRo?.disconnect();
      mq.removeEventListener('change', setup);
    },
  };
}

// ****************************************************************************************************************************************************
// SPARK patch: normalizes the search box in the header

export function modifySearchBox() {
  const $searchInput = $('.header .inputFieldTextbox');
  const $searchButton = $('.header .searchSubmitButton');

  $searchInput.attr('placeholder', 'Type here...');
  $searchButton.attr('tabindex', '-1');
  $searchButton.attr('aria-label', 'submit search');
}

// ****************************************************************************************************************************************************
// SPARK patch: collapses the Feature Lineup button row when social icons + CTA overflow the container width

export function modifyFeatureLineup() {
  const $lineups = $('.featureLineupContainer');
  if (!$lineups.length) return;

  $lineups.each((_, el) => {
    const $lineup = $(el);
    const containerWidth = $lineup.width();
    const $buttonContainer = $lineup.find('.featureLineup-buttons.subContainer');
    const $icons = $buttonContainer.find('.featureLineup-social');
    const $cta = $buttonContainer.find('.featureLineup-buynow');

    if (!$icons.children().length) {
      $icons.remove();
      return;
    }

    const totalWidth = $icons.outerWidth(true) + $cta.outerWidth(true);

    if (totalWidth > containerWidth) {
      $buttonContainer.addClass('column');
    }
  });
}

// ****************************************************************************************************************************************************
// SPARK patch: modifies the WeatherBug widget to only display the last word of the description text

export function modifyWeatherBug() {
  const widget = document.getElementById('weatherBugWidget');
  const desc = document.getElementById('weatherBugDescription');
  if (!widget || !desc) return;

  const observer = new MutationObserver(() => {
    const text = desc.textContent.trim();
    if (!text || widget.classList.contains('weatherBugSet')) return;

    widget.classList.add('weatherBugSet');

    const lastSpace = text.lastIndexOf(' ');
    if (lastSpace !== -1) {
      desc.textContent = text.substring(lastSpace + 1);
    }

    observer.disconnect();
  });

  observer.observe(desc, { childList: true, subtree: true });
}

// ****************************************************************************************************************************************************
// SPARK patch: modifies the email signup form to remove the label text and set a placeholder in the input field

export function modifySignup() {
  const $signup = $('.emailSignup');
  if (!$signup.length) return;

  const $input = $signup.find('.inputFieldTextbox');
  const $submit = $signup.find('.emailSubmitButton');

  $signup.find('.emailSignupText').remove();

  $input.val('').attr('placeholder', 'Email Address');

  $submit.text('SUBSCRIBE').attr('aria-label', 'Subscribe to newsletter');
}
